import { type ResultOf, useSession } from '@horof/shared';
import { graphql } from './gql';

/**
 * The association's card and its documents library (arqam doc 52). The
 * committee and the treasurer read and write documents; only the committee
 * edits the card (`updateSetting`).
 */

/** Refetch the library after any change to documents or their files. */
export const documentsContext = {
  additionalTypenames: [
    'AssociationDocument',
    'AssociationDocumentFile',
    'Counterparty',
    'Project',
  ],
};

export const ProfileDocument = graphql(`
  query DeskProfile {
    currentSettings {
      associationName
      shortName
      foundedOn
      address
      contactPhone
      email
      registrationNumber
      registrationDate
      registrationAuthority
      taxId
      bankRib
      mission
      vision
      mandateStart
      mandateEnd
      updatedAt
    }
    members(
      filter: { committeeRole: { isNull: false } }
      orderBy: [LAST_NAME_ASC, FIRST_NAME_ASC]
    ) {
      nodes {
        id
        fullName
        phone
        committeeRole
      }
    }
  }
`);

export type Profile = NonNullable<
  ResultOf<typeof ProfileDocument>['currentSettings']
>;

export const UpdateProfileDocument = graphql(`
  mutation DeskUpdateProfile($patch: SettingPatch!) {
    updateSetting(input: { id: true, patch: $patch }) {
      setting {
        associationName
        shortName
        foundedOn
        address
        contactPhone
        email
        registrationNumber
        registrationDate
        registrationAuthority
        taxId
        bankRib
        mission
        vision
        mandateStart
        mandateEnd
        updatedAt
      }
    }
  }
`);

export const DocumentsDocument = graphql(`
  query DeskDocuments($filter: AssociationDocumentFilter) {
    associationDocuments(
      filter: $filter
      orderBy: [DOCUMENT_DATE_DESC, CREATED_AT_DESC]
    ) {
      totalCount
      nodes {
        id
        type
        title
        documentDate
        reference
        isCurrent
        replyDueOn
        repliedById
        counterparty {
          id
          name
        }
        project {
          id
          name
        }
        files: associationDocumentFilesByDocumentId {
          totalCount
        }
      }
    }
  }
`);

export type DocumentRow = NonNullable<
  NonNullable<
    ResultOf<typeof DocumentsDocument>['associationDocuments']
  >['nodes'][number]
>;

export const DocumentDocument = graphql(`
  query DeskDocument($id: UUID!) {
    associationDocument(id: $id) {
      id
      type
      title
      documentDate
      reference
      counterpartyId
      projectId
      notes
      replyDueOn
      repliedById
      isCurrent
      createdAt
      createdBy
      repliedBy {
        id
        type
        title
        reference
        documentDate
      }
      replyTo: associationDocumentsByRepliedById {
        nodes {
          id
          type
          title
          reference
          documentDate
        }
      }
      files: associationDocumentFilesByDocumentId(orderBy: [POSITION_ASC]) {
        nodes {
          id
          position
          photoId
          photo {
            id
            url
            contentType
          }
        }
      }
    }
  }
`);

export type DocumentDetail = NonNullable<
  ResultOf<typeof DocumentDocument>['associationDocument']
>;

/** Letters of one direction, for «هذا رد على…». */
export const LettersDocument = graphql(`
  query DeskLetters($type: AssociationDocumentType!) {
    associationDocuments(
      condition: { type: $type }
      orderBy: [DOCUMENT_DATE_DESC]
      first: 200
    ) {
      nodes {
        id
        title
        reference
        documentDate
        repliedById
      }
    }
  }
`);

export const CreateDocumentDocument = graphql(`
  mutation DeskCreateDocument($doc: AssociationDocumentInput!) {
    createAssociationDocument(input: { associationDocument: $doc }) {
      associationDocument {
        id
        reference
      }
    }
  }
`);

export const UpdateDocumentDocument = graphql(`
  mutation DeskUpdateDocument($id: UUID!, $patch: AssociationDocumentPatch!) {
    updateAssociationDocument(input: { id: $id, patch: $patch }) {
      associationDocument {
        id
        reference
        repliedById
      }
    }
  }
`);

export const DeleteDocumentDocument = graphql(`
  mutation DeskDeleteDocument($id: UUID!) {
    deleteAssociationDocument(input: { id: $id }) {
      associationDocument {
        id
      }
    }
  }
`);

export const AddFileDocument = graphql(`
  mutation DeskAddDocumentFile($file: AssociationDocumentFileInput!) {
    createAssociationDocumentFile(input: { associationDocumentFile: $file }) {
      associationDocumentFile {
        id
        position
      }
    }
  }
`);

export const MoveFileDocument = graphql(`
  mutation DeskMoveDocumentFile($id: UUID!, $position: Int!) {
    updateAssociationDocumentFile(
      input: { id: $id, patch: { position: $position } }
    ) {
      associationDocumentFile {
        id
        position
      }
    }
  }
`);

export const RemoveFileDocument = graphql(`
  mutation DeskRemoveDocumentFile($id: UUID!) {
    deleteAssociationDocumentFile(input: { id: $id }) {
      associationDocumentFile {
        id
      }
    }
  }
`);

/** The committee and the treasurer write documents. */
export function useCanFile(): boolean {
  const { roles } = useSession();
  return roles.includes('committee') || roles.includes('treasurer');
}
